import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from '../../lib/supabase';
import { scheduleIdleTask } from '../../utils/idleCallback';
import EventCard from '../discover/EventCard';
import Loading from '../layout/Loading';
import EventEmptyStateIcon from './EventEmptyStateIcon';
import './HistoryView.css';

function HistoryView({ refreshKey = 0, isActive = true }) {
  const { t } = useTranslation('events');
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const channelRef = useRef(null);
  const refreshKeyRef = useRef(refreshKey);
  refreshKeyRef.current = refreshKey;
  const loadedRefreshKeyRef = useRef(null);

  const loadHistoryEvents = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Get current user
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setLoading(false);
        return;
      }

      // Load events where user is host
      // We'll load all events and filter in JavaScript to handle both cancelled and ended events
      let hostedQuery = supabase
        .from('events')
        .select('*')
        .eq('host_id', user.id)
        .order('event_date', { ascending: false })
        .order('start_time', { ascending: false });

      let { data: hostedEvents, error: hostedError } = await hostedQuery;

      // If error is about missing column, retry without filter
      if (hostedError && hostedError.message && hostedError.message.includes('is_cancelled')) {
        const retryResult = await supabase
          .from('events')
          .select('*')
          .eq('host_id', user.id)
          .order('event_date', { ascending: false })
          .order('start_time', { ascending: false });
        hostedEvents = retryResult.data;
        hostedError = retryResult.error;
      }

      if (hostedError) throw hostedError;

      // Filter hosted events: cancelled OR ended (check if event_date + duration < now)
      const now = new Date();
      const filteredHostedEvents = (hostedEvents || []).filter(event => {
        // Include if cancelled
        if (event.is_cancelled === true) return true;
        
        // Check if event has ended (event_date + duration < now)
        const eventStartDateTime = new Date(`${event.event_date}T${event.start_time}`);
        const eventEndDateTime = new Date(eventStartDateTime);
        eventEndDateTime.setMinutes(eventEndDateTime.getMinutes() + (event.duration_minutes || 0));
        
        return now >= eventEndDateTime;
      });

      // Get events where user is participant with ACTIVE status only
      // Exclude kicked or withdrawn participants
      // We'll load all and filter in JavaScript to handle both cancelled and ended events
      let participantQuery = supabase
        .from('event_participants')
        .select('event_id, events!inner (*)')
        .eq('user_id', user.id)
        .eq('status', 'active');

      let { data: participantEvents, error: participantError } = await participantQuery;

      if (participantError) throw participantError;

      // Combine and deduplicate events
      const allEvents = [];
      const eventIds = new Set();

      // Add hosted events
      if (filteredHostedEvents) {
        filteredHostedEvents.forEach(event => {
          if (!eventIds.has(event.id)) {
            eventIds.add(event.id);
            allEvents.push(event);
          }
        });
      }

      // Add participant events (only active participants, cancelled or ended)
      if (participantEvents) {
        participantEvents.forEach(({ events: event }) => {
          if (event && !eventIds.has(event.id)) {
            // Include if cancelled
            if (event.is_cancelled === true) {
              eventIds.add(event.id);
              allEvents.push(event);
              return;
            }
            
            // Check if event has ended (event_date + duration < now)
            const now = new Date();
            const eventStartDateTime = new Date(`${event.event_date}T${event.start_time}`);
            const eventEndDateTime = new Date(eventStartDateTime);
            eventEndDateTime.setMinutes(eventEndDateTime.getMinutes() + (event.duration_minutes || 0));
            
            if (now >= eventEndDateTime) {
              eventIds.add(event.id);
              allEvents.push(event);
            }
          }
        });
      }

      // Sort by date and time (newest first)
      allEvents.sort((a, b) => {
        const dateA = new Date(`${a.event_date}T${a.start_time}`);
        const dateB = new Date(`${b.event_date}T${b.start_time}`);
        return dateB - dateA; // Reverse order for history
      });

      setEvents(allEvents);
      loadedRefreshKeyRef.current = refreshKeyRef.current;
    } catch (err) {
      console.error('Error loading history events:', err);
      setError(err.message || t('events.history.error', 'Wystąpił błąd podczas ładowania historii wydarzeń.'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    let cancelled = false;
    let idleTask = null;

    const cleanupChannel = () => {
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
        channelRef.current = null;
      }
    };

    if (!isActive) {
      cleanupChannel();
      if (loadedRefreshKeyRef.current !== refreshKey) {
        idleTask = scheduleIdleTask(() => {
          if (cancelled) return;
          if (loadedRefreshKeyRef.current === refreshKey) return;
          loadHistoryEvents();
        }, { timeout: 20000 });
      }
      return () => {
        cancelled = true;
        if (idleTask) idleTask.cancel();
      };
    }

    if (loadedRefreshKeyRef.current !== refreshKey) {
      loadHistoryEvents();
    }

    const setupRealtimeSubscription = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user || cancelled) return;

      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
      }

      const channel = supabase
        .channel(`history-participants-${user.id}`)
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'event_participants',
            filter: `user_id=eq.${user.id}`
          },
          async (payload) => {
            console.log('Participant change detected in HistoryView:', payload);
            loadHistoryEvents();
          }
        )
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'events',
            filter: `host_id=eq.${user.id}`
          },
          async (payload) => {
            console.log('Event change detected in HistoryView (user is host):', payload);
            loadHistoryEvents();
          }
        )
        .subscribe();

      if (!cancelled) {
        channelRef.current = channel;
      } else {
        supabase.removeChannel(channel);
      }
    };

    setupRealtimeSubscription();

    return () => {
      cancelled = true;
      cleanupChannel();
    };
  }, [isActive, refreshKey, loadHistoryEvents]);

  if (loading) {
    return (
      <div className="history-view">
        <Loading 
          className="history-view-loading" 
          size="medium"
          message={t('events.history.loading', 'Ładowanie historii wydarzeń...')}
        />
      </div>
    );
  }

  if (error) {
    return (
      <div className="history-view">
        <div className="history-view-error">
          <p>{error}</p>
        </div>
      </div>
    );
  }

  if (events.length === 0) {
    return (
      <div className="history-view">
        <div className="history-view-empty history-view-empty-card">
          <div className="history-view-empty-icon" aria-hidden="true">
            <EventEmptyStateIcon variant="history" />
          </div>
          <h2>{t('events.history.title')}</h2>
          <p>{t('events.history.description')}</p>
          <span className="history-view-empty-tip">
            {t('events.history.emptyTip', 'Po zakończeniu wydarzeń ich historia będzie widoczna właśnie tutaj.')}
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="history-view">
      <div className="history-view-header">
        <div className="history-view-header-left">
          <h2>{t('events.history.title')}</h2>
          <p className="history-view-count">
            {events.length} {t('events.history.eventsCount', 'wydarzeń w historii')}
          </p>
        </div>
      </div>
      <div className="history-view-events">
        {events.map((event) => (
          <EventCard key={event.id} event={event} />
        ))}
      </div>
    </div>
  );
}

export default HistoryView;
